import { Percent, StackedBarChart, StackedLineChart } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
} from "@mui/joy";
import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

import { useVenueArchiveRoutesTimeSeriesQuery } from "@/hooks/routes";
import { useCurrentClimbType, useCurrentVenueUuid } from "@/hooks/venues";

import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";
import TimeSeriesGraph, { TimeSeriesGraphType } from "../TimeSeriesGraph/TimeSeriesGraph";
import { ArchiveAnalysisTypeSelector } from "./ArchiveGraphs";
import { convertArchiveSortFilterParamsToBackend } from "./convertArchiveSortFilterParamsToBackend";

type ArchiveGraphType = Exclude<TimeSeriesGraphType, "line">;

const graphTypeOptions: Record<ArchiveGraphType, { label: string; icon: JSX.Element }> = {
  // line: {
  //   label: "Line",
  //   icon: <ShowChart />,
  // },
  stackedBar: {
    label: "Stacked Bar",
    icon: <StackedBarChart />,
  },
  stackedArea: {
    label: "Stacked Area",
    icon: <StackedLineChart />,
  },
  stacked100Bar: {
    label: "100% Stacked Bar",
    icon: <Percent />,
  },
  stacked100Area: {
    label: "100% Stacked Area",
    icon: <Percent />,
  },
};

const periodOptionLabels = {
  day: "Day",
  week: "Week",
  month: "Month",
};

export const ArchiveTimeSeries = () => {
  const filterParams = useRoutesTableStore((state) => state.filterParams);
  const [debouncedFilterParams] = useDebounce(filterParams, 500);
  const apiFilterParams = useMemo(
    () => convertArchiveSortFilterParamsToBackend(debouncedFilterParams, []),
    [debouncedFilterParams],
  );
  const climbType = useCurrentClimbType();
  const venueUuid = useCurrentVenueUuid();

  const segmentOptionLabels = useMemo(
    () => ({
      grade: "Grade",
      setter: "Setter",
      ...(climbType === "BOULDER" ? { circuit: "Circuit" } : {}),
      zone: climbType === "BOULDER" ? "Zone" : "Sector",
      style: "Style",
      skill: "Skill",
      hold: "Hold",
    }),
    [climbType],
  );

  const [graphType, setGraphType] = useState<ArchiveGraphType>("stackedArea");
  const [period, setPeriod] = useState<"day" | "week" | "month">("month");

  const [segment, setSegment] = useState<keyof typeof segmentOptionLabels>(
    climbType === "BOULDER" ? "circuit" : "setter",
  );

  const [withComparison, setWithComparison] = useState<boolean>(false);

  // const data = useMemo(
  //   () => generateTestSeries(period, new Date(2024, 1, 1), new Date()),
  //   [period]
  // );

  // const testData: TimeSeriesData = {
  //   columns: [
  //     {
  //       label: "James B",
  //       key: "james-b",
  //       colors: "red",
  //       data: data,
  //     },
  //     {
  //       label: "John S",
  //       key: "john-s",
  //       colors: "blue",
  //       data: data,
  //     },
  //   ],
  //   totals: data.map((point) => ({
  //     x: point.x,
  //     y: point.y * 2,
  //   })),
  //   yLabel: "Routes Set",
  // };

  const timeSeriesQuery = useVenueArchiveRoutesTimeSeriesQuery(
    venueUuid,
    climbType,
    {
      segment,
      period,
      stack: ["stackedBar", "stacked100Bar", "stackedArea", "stacked100Area"].includes(graphType),
      stackNormalizeUnit: graphType === "stacked100Area" || graphType === "stacked100Bar",
      withComparison,
    },
    apiFilterParams,
  );

  return (
    <Stack sx={{ display: "flex", height: "100%" }}>
      <Stack direction="row" padding={1} spacing={1} alignItems="center">
        <ArchiveAnalysisTypeSelector />
        <Box flex={1} />
        <Checkbox
          label="Compare previous year"
          size="sm"
          checked={withComparison}
          onChange={(e) => setWithComparison(e.target.checked)}
        />
        <Dropdown>
          <MenuButton size="sm">Breakdown: {segmentOptionLabels[segment]}</MenuButton>
          <Menu>
            {Object.entries(segmentOptionLabels).map(([key, value]) => (
              <MenuItem
                key={key}
                onClick={() => setSegment(key as keyof typeof segmentOptionLabels)}
              >
                {value}
              </MenuItem>
            ))}
          </Menu>
        </Dropdown>
        <Dropdown>
          <MenuButton size="sm">Period: {periodOptionLabels[period]}</MenuButton>
          <Menu>
            {Object.entries(periodOptionLabels).map(([key, value]) => (
              <MenuItem key={key} onClick={() => setPeriod(key as "day" | "week" | "month")}>
                {value}
              </MenuItem>
            ))}
          </Menu>
        </Dropdown>
        <Dropdown>
          <MenuButton size="sm" startDecorator={graphTypeOptions[graphType].icon}>
            {graphTypeOptions[graphType].label}
          </MenuButton>
          <Menu>
            {Object.entries(graphTypeOptions).map(([key, value]) => (
              <MenuItem key={key} onClick={() => setGraphType(key as ArchiveGraphType)}>
                {value.icon}
                {value.label}
              </MenuItem>
            ))}
          </Menu>
        </Dropdown>
      </Stack>
      {timeSeriesQuery.isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size="md" thickness={4} />
        </Box>
      ) : null}
      {timeSeriesQuery.data ? (
        <TimeSeriesGraph data={timeSeriesQuery.data} type={graphType} period={period} />
      ) : null}
    </Stack>
  );
};
